import { useDraggable, useDroppable, useDndContext } from '@dnd-kit/core';
import { Card } from 'react-bootstrap';
import type { LeadCloudFileType } from '@econtractor/types';
import SolarFolder2BoldDuotone from 'components/icons/SolarFolder2BoldDuotone';
import SpinnerBlock from 'components/others/SpinnerBlock';
import ItemDropdown from './ItemDropdown';
import {
    canDropOnParent,
    folderDroppableId,
    itemFromDraggable,
    type FileDragData,
    type FileDropData,
} from './model/fileDropRules';
import { itemFolderPath } from './model/folderTree';

interface FolderItemProps {
    folder: LeadCloudFileType;
    busy?: boolean;
    canDelete?: boolean;
    onOpen: (folder: LeadCloudFileType) => void;
    onDelete?: (folder: LeadCloudFileType) => void;
    onRename?: (folder: LeadCloudFileType) => void;
    canDrag?: boolean;
    overlay?: boolean;
}

const FolderItem = ({
    folder,
    busy,
    canDelete,
    onOpen,
    onDelete,
    onRename,
    canDrag,
    overlay,
}: FolderItemProps) => {
    const dragEnabled = Boolean(canDrag) && !overlay;
    const folderPath = itemFolderPath(folder);

    const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
        id: overlay ? `overlay:${folder.storageKey}` : folder.storageKey,
        data: { type: 'folder', item: folder } satisfies FileDragData,
        disabled: !dragEnabled,
    });
    const { setNodeRef: setDropRef, isOver } = useDroppable({
        id: overlay ? `overlay-drop:${folderPath}` : folderDroppableId(folderPath),
        data: { type: 'folder', parentPath: folderPath } satisfies FileDropData,
        disabled: !dragEnabled,
    });
    const { active } = useDndContext();
    const activeItem = itemFromDraggable(active?.data.current);
    const dropOk = Boolean(dragEnabled && isOver && !isDragging && canDropOnParent(activeItem, folderPath));

    const setNodeRef = (node: HTMLElement | null) => {
        setDragRef(node);
        setDropRef(node);
    };

    const cardClass = [
        'file-card m-0 position-relative',
        dragEnabled ? 'file-card--draggable' : '',
        isDragging ? 'file-card--dragging' : '',
        dropOk ? 'file-card--drop-target' : '',
        overlay ? 'file-card--overlay' : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div
            ref={setNodeRef}
            className={[dragEnabled ? 'file-card-dnd' : '', isDragging ? 'file-card--dragging' : '']
                .filter(Boolean)
                .join(' ') || undefined}
            {...(dragEnabled ? { ...attributes, ...listeners } : {})}
        >
            <Card className={cardClass}>
                <SpinnerBlock show={busy} />
                <Card.Body className="p-2 file-card-body file-card-body--folder">
                    <button
                        type="button"
                        className="btn btn-link text-decoration-none text-body p-0 flex-shrink-0"
                        onClick={() => {
                            if (busy || active) return;
                            onOpen(folder);
                        }}
                        title={folder.fileName}
                    >
                        <SolarFolder2BoldDuotone className="file-folder-icon" />
                    </button>
                    <button
                        type="button"
                        className="btn btn-link text-decoration-none text-body p-0 file-card-meta d-flex align-items-center min-w-0"
                        onClick={() => {
                            if (busy || active) return;
                            onOpen(folder);
                        }}
                        title={folder.fileName}
                    >
                        <h5 className="mb-0 d-grid min-w-0">
                            <span className="text-truncate w-100 fs-14 fw-normal no-select">{folder.fileName}</span>
                        </h5>
                    </button>
                    {overlay ? null : (
                        <ItemDropdown
                            canDelete={canDelete}
                            canRename={canDelete}
                            deleting={busy}
                            onDelete={() => onDelete?.(folder)}
                            onRename={() => onRename?.(folder)}
                        />
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default FolderItem;
